import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Container,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Tooltip,
  Switch,
  MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import { useCustomToast, ToastVariant } from '../../components/Methods/SnackBar';
import axios from 'axios';
import { apiEndpoint } from '../../Service/Apis';
import Loading from '../../components/Loading/Loading';
import PageTitle from '../../components/Page Component/PageTitle';

const GOOGLE_SHEETS_PREFIX = 'https://docs.google.com/spreadsheets/';

const isValidGoogleSheetsUrl = (url: string): boolean => url.startsWith(GOOGLE_SHEETS_PREFIX);

interface Training {
  id: string;
  training_name: string;
  template_sheet_url: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface FeedbackQuestion {
  id: string;
  question: string;
  question_type: 'stars' | 'comment';
  order: number;
  is_active: boolean;
  is_reviewed: boolean;
  created_at: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const TrainingMaster: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [trainings, setTrainings] = useState<Training[]>([]);
  const [questions, setQuestions] = useState<FeedbackQuestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [openTrainingDialog, setOpenTrainingDialog] = useState(false);
  const [openQuestionDialog, setOpenQuestionDialog] = useState(false);
  const [editingTraining, setEditingTraining] = useState<Training | null>(null);
  const [editingQuestion, setEditingQuestion] = useState<FeedbackQuestion | null>(null);
  
  const [trainingForm, setTrainingForm] = useState({
    training_name: '',
    template_sheet_url: '',
  });
  
  const [questionForm, setQuestionForm] = useState({
    question: '',
    question_type: 'stars' as 'stars' | 'comment',
    order: 0,
  });

  const showToast = useCustomToast();

  useEffect(() => {
    fetchTrainings();
    fetchQuestions();
  }, []);

  const fetchTrainings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiEndpoint}/skillset/training/master/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      setTrainings(response.data);
    } catch (error: any) {
      showToast(error.response?.data?.detail || 'Failed to fetch trainings', ToastVariant.ERROR, true);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiEndpoint}/skillset/training/feedback-questions/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });
      setQuestions(response.data);
    } catch (error: any) {
      showToast(error.response?.data?.detail || 'Failed to fetch feedback questions', ToastVariant.ERROR, true);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleOpenTrainingDialog = (training?: Training) => {
    if (training) {
      setEditingTraining(training);
      setTrainingForm({
        training_name: training.training_name,
        template_sheet_url: training.template_sheet_url,
      });
    } else {
      setEditingTraining(null);
      setTrainingForm({ training_name: '', template_sheet_url: '' });
    }
    setOpenTrainingDialog(true);
  };

  const handleOpenQuestionDialog = (question?: FeedbackQuestion) => {
    if (question) {
      setEditingQuestion(question);
      setQuestionForm({
        question: question.question,
        question_type: question.question_type,
        order: question.order,
      });
    } else {
      setEditingQuestion(null);
      const maxOrder = questions.length > 0 ? Math.max(...questions.map(q => q.order)) : 0;
      setQuestionForm({ question: '', question_type: 'stars', order: maxOrder + 1 });
    }
    setOpenQuestionDialog(true);
  };

  const handleSaveTraining = async () => {
    if (!trainingForm.template_sheet_url || !isValidGoogleSheetsUrl(trainingForm.template_sheet_url)) {
      showToast('Please enter a valid Google Sheets URL', ToastVariant.ERROR, true);
      return;
    }
    try {
      if (editingTraining) {
        await axios.patch(
          `${apiEndpoint}/skillset/training/master/${editingTraining.id}/`,
          trainingForm,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
            },
          }
        );
        showToast('Training updated successfully', ToastVariant.SUCCESS, true);
      } else {
        await axios.post(
          `${apiEndpoint}/skillset/training/master/`,
          trainingForm,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
            },
          }
        );
        showToast('Training created successfully', ToastVariant.SUCCESS, true);
      }
      fetchTrainings();
      setOpenTrainingDialog(false);
    } catch (error: any) {
      showToast(error.response?.data?.detail || 'Failed to save training', ToastVariant.ERROR, true);
    }
  };

  const handleToggleTraining = async (id: string, currentStatus: boolean) => {
    try {
      await axios.patch(
        `${apiEndpoint}/skillset/training/master/${id}/`,
        { is_active: !currentStatus },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      showToast(`Training ${!currentStatus ? 'activated' : 'deactivated'} successfully`, ToastVariant.SUCCESS, true);
      fetchTrainings();
    } catch (error: any) {
      showToast(error.response?.data?.detail || 'Failed to toggle training status', ToastVariant.ERROR, true);
    }
  };

  const handleToggleQuestion = async (id: string, currentStatus: boolean) => {
    try {
      await axios.patch(
        `${apiEndpoint}/skillset/training/feedback-questions/${id}/`,
        { is_active: !currentStatus },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      showToast(`Question ${!currentStatus ? 'activated' : 'deactivated'} successfully`, ToastVariant.SUCCESS, true);
      fetchQuestions();
    } catch (error: any) {
      showToast(error.response?.data?.detail || 'Failed to toggle question status', ToastVariant.ERROR, true);
    }
  };

  const handleQuestionDragEnd = async (result: DropResult) => {
    if (!result.destination) return;
    
    const items = Array.from(questions);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    const updatedItems = items.map((item, index) => ({ ...item, order: index }));
    setQuestions(updatedItems);
    
    try {
      await axios.post(
        `${apiEndpoint}/skillset/training/feedback-questions/reorder/`,
        {
          question_orders: updatedItems.map(q => ({ id: q.id, order: q.order }))
        },
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      showToast('Questions reordered successfully', ToastVariant.SUCCESS, true);
    } catch (error: any) {
      fetchQuestions(); // Revert on error
      showToast(error.response?.data?.detail || 'Failed to reorder questions', ToastVariant.ERROR, true);
    }
  };

  const handleSaveQuestion = async () => {
    try {
      if (editingQuestion) {
        await axios.patch(
          `${apiEndpoint}/skillset/training/feedback-questions/${editingQuestion.id}/`,
          questionForm,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
            },
          }
        );
        showToast('Question updated successfully', ToastVariant.SUCCESS, true);
      } else {
        await axios.post(
          `${apiEndpoint}/skillset/training/feedback-questions/`,
          questionForm,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
            },
          }
        );
        showToast('Question created successfully', ToastVariant.SUCCESS, true);
      }
      fetchQuestions();
      setOpenQuestionDialog(false);
    } catch (error: any) {
      showToast(error.response?.data?.detail || error.response?.data?.non_field_errors?.[0] || 'Failed to save question', ToastVariant.ERROR, true);
    }
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 12, mb: 10 }}>
      <Box sx={{ mb: 3 }}>
        <PageTitle>Training Master</PageTitle>
      </Box>

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Training Masters" />
            <Tab label="Feedback Questions" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Training Masters</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenTrainingDialog()}
            >
              Add Training
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Training Name</TableCell>
                  <TableCell>Template URL</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell align="center">Active</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {trainings.map((training) => (
                  <TableRow key={training.id}>
                    <TableCell>{training.training_name}</TableCell>
                    <TableCell>
                      <a href={training.template_sheet_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.875rem' }}>
                        View Sheet
                      </a>
                    </TableCell>
                    <TableCell>{new Date(training.created_at).toLocaleDateString()}</TableCell>
                    <TableCell align="center">
                      <Switch
                        checked={training.is_active}
                        onChange={() => handleToggleTraining(training.id, training.is_active)}
                        color="primary"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Edit">
                        <IconButton onClick={() => handleOpenTrainingDialog(training)} size="small">
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Manager Feedback Questions</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenQuestionDialog()}
            >
              Add Question
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <DragDropContext onDragEnd={handleQuestionDragEnd}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Order</TableCell>
                    <TableCell>Question</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="center">Active</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <Droppable droppableId="questions">
                  {(provided) => (
                    <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                      {questions.map((question, index) => (
                        <Draggable
                          key={question.id}
                          draggableId={question.id}
                          index={index}
                          isDragDisabled={question.is_reviewed || !question.is_active}
                        >
                          {(provided, snapshot) => (
                            <TableRow
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              style={{
                                ...provided.draggableProps.style,
                                backgroundColor: snapshot.isDragging ? '#f5f5f5' : 'inherit',
                                cursor: question.is_reviewed || !question.is_active ? 'default' : 'grab',
                                opacity: question.is_active ? 1 : 0.5,
                              }}
                            >
                              <TableCell>{question.order}</TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography
                                    variant="body2"
                                    sx={question.is_active ? undefined : { textDecoration: 'line-through', color: 'text.disabled' }}
                                  >
                                    {question.question}
                                  </Typography>
                                  {!question.is_active && (
                                    <Chip label="Inactive" size="small" color="default" variant="outlined" />
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  label={question.question_type === 'stars' ? 'Star Rating' : 'Comment'}
                                  color={question.question_type === 'stars' ? 'primary' : 'secondary'}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Switch
                                  checked={question.is_active}
                                  onChange={() => handleToggleQuestion(question.id, question.is_active)}
                                  color="primary"
                                  disabled={question.is_reviewed}
                                />
                              </TableCell>
                              <TableCell align="center">
                                {!question.is_reviewed ? (
                                  <Tooltip title={question.is_active ? 'Edit' : 'Activate first to edit'}>
                                    <span>
                                      <IconButton
                                        onClick={() => handleOpenQuestionDialog(question)}
                                        size="small"
                                        disabled={!question.is_active}
                                      >
                                        <EditIcon />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                ) : (
                                  <Tooltip title="Cannot edit - used in feedback">
                                    <span>
                                      <IconButton disabled size="small">
                                        <EditIcon />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                )}
                              </TableCell>
                            </TableRow>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </TableBody>
                  )}
                </Droppable>
              </Table>
            </DragDropContext>
          </TableContainer>
        </TabPanel>
      </Card>

      {/* Training Dialog */}
      <Dialog open={openTrainingDialog} onClose={() => setOpenTrainingDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingTraining ? 'Edit Training' : 'Add Training'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Training Name"
            type="text"
            fullWidth
            variant="outlined"
            value={trainingForm.training_name}
            onChange={(e) => setTrainingForm({ ...trainingForm, training_name: e.target.value })}
            required
          />
          <TextField
            margin="dense"
            label="Template Google Sheet URL"
            type="url"
            fullWidth
            variant="outlined"
            value={trainingForm.template_sheet_url}
            onChange={(e) => setTrainingForm({ ...trainingForm, template_sheet_url: e.target.value })}
            required
            error={!!trainingForm.template_sheet_url && !isValidGoogleSheetsUrl(trainingForm.template_sheet_url)}
            helperText={
              trainingForm.template_sheet_url && !isValidGoogleSheetsUrl(trainingForm.template_sheet_url)
                ? 'URL must be a Valid Google Sheets link (https://docs.google.com/spreadsheets/...)'
                : ''
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTrainingDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveTraining} variant="contained" disabled={!trainingForm.training_name.trim() || !isValidGoogleSheetsUrl(trainingForm.template_sheet_url)}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Question Dialog */}
      <Dialog open={openQuestionDialog} onClose={() => setOpenQuestionDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingQuestion ? 'Edit Question' : 'Add Question'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Question"
            type="text"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={questionForm.question}
            onChange={(e) => setQuestionForm({ ...questionForm, question: e.target.value })}
            required
          />
          <TextField
            select
            margin="dense"
            label="Question Type"
            fullWidth
            variant="outlined"
            value={questionForm.question_type}
            onChange={(e) => setQuestionForm({ ...questionForm, question_type: e.target.value as 'stars' | 'comment' })}
            required
            sx={{ mt: 2 }}
          >
            <MenuItem value="stars">Star Rating (1-5)</MenuItem>
            <MenuItem value="comment">Comment</MenuItem>
          </TextField>
          <TextField
            margin="dense"
            label="Order"
            type="number"
            fullWidth
            variant="outlined"
            value={questionForm.order}
            onChange={(e) => setQuestionForm({ ...questionForm, order: parseInt(e.target.value) })}
            required
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenQuestionDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveQuestion} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TrainingMaster;
